import React, { useState, useEffect } from "react";
import "./ConfigureDropDown.scss";
import toast from "react-hot-toast";
import api from "components/api/api";

const ConfigureDropDown = ({
  isOpen,
  toggle,
  surveyId,
  onConfigSave,
  userDataDisplayFields = [], // Receive the existing fields from parent
}) => {
  const [hiddenFields, setHiddenFields] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Only these 4 specific fields
  const fields = ["Staff Name", "Region", "Outlet", "Staff ID"];

  // Field mapping between display names and API field names
  const fieldMapping = {
    "Staff Name": "username",
    Region: "region",
    Outlet: "outlet",
    "Staff ID": "staffId",
  };

  // Reverse mapping for API field names to display names
  const reverseFieldMapping = {
    username: "Staff Name",
    region: "Region",
    outlet: "Outlet",
    staffId: "Staff ID",
  };

  // Sample data for preview
  const sampleUserData = {
    "Staff Name": "HAMED MOHAMMED AHMED AL SHAHRI",
    Region: "DHOFAR",
    Outlet: "HQLULU AL BURAIMI OUTLET",
    "Staff ID": "69925",
  };

  // Initialize hiddenFields based on userDataDisplayFields when modal opens
  useEffect(() => {
    if (isOpen && userDataDisplayFields.length > 0) {
      // Convert API field names to display names
      const visibleDisplayFields = userDataDisplayFields
        .map((apiField) => reverseFieldMapping[apiField])
        .filter(Boolean); // Filter out any undefined values

      // Calculate which fields should be hidden
      const fieldsToHide = fields.filter(
        (field) => !visibleDisplayFields.includes(field)
      );

      setHiddenFields(fieldsToHide);
    } else if (isOpen) {
      // If no userDataDisplayFields provided, show all fields by default
      setHiddenFields([]);
    }
  }, [isOpen, userDataDisplayFields]);

  const handleToggleField = (field) => {
    setHiddenFields((prev) => {
      const newHiddenFields = prev.includes(field)
        ? prev.filter((f) => f !== field)
        : [...prev, field];

      // Prevent hiding all fields - at least one must remain visible
      if (newHiddenFields.length >= fields.length) {
        toast.error("At least one field must remain visible!");
        return prev; // Don't allow the change
      }

      return newHiddenFields;
    });
  };

  const handleApplySettings = async () => {
    setIsLoading(true);

    const visibleFields = fields.filter((f) => !hiddenFields.includes(f));

    // Map to API field names
    const userDataDisplayFieldsToSend = visibleFields.map(
      (field) => fieldMapping[field]
    );

    try {
      await api.updateSurveyConfig(surveyId, userDataDisplayFieldsToSend);
      toast.success("Configuration saved successfully!");

      if (onConfigSave) {
        onConfigSave({
          surveyId: surveyId,
          hiddenFields: hiddenFields,
          visibleFields: visibleFields,
          userDataDisplayFields: userDataDisplayFieldsToSend,
        });
      }

      toggle();
    } catch (error) {
      console.error("Error saving configuration:", error);
      toast.error("Failed to save configuration");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null; // Don't render if not open

  const visibleFields = fields.filter((f) => !hiddenFields.includes(f));

  return (
    <div className="custom-modal-overlay" onClick={toggle}>
      <div
        className="custom-modal"
        onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
      >
        <div className="custom-modal-header">
          <h3>Configure Data Visibility</h3>
          <button className="close-btn" onClick={toggle}>
            &times;
          </button>
        </div>

        <div className="custom-modal-body">
          <div className="field-controls">
            <h5>Select Fields to Display</h5>
            <div className="note-message">
              <span className="note-icon">ℹ️</span>
              <span>
                Note: At least one field must remain selected for data
                visibility.
              </span>
            </div>
          </div>

          <div className="fields-section">
            {fields.map((field) => (
              <div key={field} className="checkbox-field">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={!hiddenFields.includes(field)}
                    onChange={() => handleToggleField(field)}
                    className="field-checkbox"
                  />
                  <span className="field-name">{field}</span>
                </label>
              </div>
            ))}
          </div>

          <div className="preview-section">
            <h5>Data Preview</h5>
            {visibleFields.length > 0 ? (
              <div className="visible-data">
                <div className="data-header">
                  <strong>Visible Information:</strong>
                </div>
                <div className="data-grid">
                  {visibleFields.map((field) => (
                    <div key={field} className="data-item">
                      <span className="field-label">{field}:</span>
                      <span className="field-value">
                        {sampleUserData[field]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="warning-message">
                <span className="warning-icon">⚠️</span>
                <span>No fields selected. All data will be anonymous.</span>
              </div>
            )}
          </div>
        </div>

        <div className="custom-modal-footer">
          <div className="footer-info">
            <span>
              Showing {visibleFields.length} of {fields.length} fields
            </span>
          </div>
          <div className="footer-buttons">
            <button className="btn secondary" onClick={toggle}>
              Cancel
            </button>
            <button
              className="btn primary"
              onClick={handleApplySettings}
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Apply Settings"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigureDropDown;
